import { useEffect, useMemo, useState } from 'react';

import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';
import useDisableScroll from 'hooks/useDisableScroll';

import CommonButton from 'components/common/button';
import { DragUploadButton } from 'components/dragUpload/dragUploadButton';
import { DragUploadZone } from 'components/dragUpload/dragUploadZone';

import { sendAnalyticEvent } from 'data/actions/analytics';
import { toggleModal } from 'data/actions/modals';
import { modalOptionsSelector } from 'data/selectors/modals';

import type { InternalFileType } from 'ts/types/file';

import ModalLayout from '../baseModal/base';
import { ReactComponent as DownloadIcon } from './assets/download.svg';
import { PreviewSwitcher } from './components/previewSwitcher';
import { SliderPreview } from './components/sliderPreview';
import { SplitScreenPreview } from './components/splitScreenPreview';
import { EViewMode } from './constants';

interface IRemoveWatermarkModalOptions {
  removedWatermarkImageUrl: string;
  originalImageUrl: string;
  acceptFormats: InternalFileType[];
  onDownload: () => void;
  handleUploadFile: (file: File) => void;
}

export const RemoveWatermarkModal = () => {
  useDisableScroll();

  const {
    removedWatermarkImageUrl,
    originalImageUrl,
    acceptFormats,
    onDownload,
    handleUploadFile
  }: IRemoveWatermarkModalOptions = useSelector(modalOptionsSelector);
  const dispatch = useDispatch();

  const [viewMode, setViewMode] = useState<EViewMode>(EViewMode.SPLIT_SCREEN);

  const formats = useMemo(() => {
    return acceptFormats.map((format) => `.${format.toLowerCase()}`).join(', ');
  }, [acceptFormats]);

  const handleUpload = (file: File, type: 'upload' | 'drop' = 'drop') => {
    dispatch(
      sendAnalyticEvent({
        event: 'upload_file_tap',
        data: { place: 'watermark_modal', method: type === 'upload' ? 'click' : 'drag_and_drop' }
      })
    );
    dispatch(toggleModal({ visible: false }));
    handleUploadFile(file);
  };

  const onViewModeChange = (value: EViewMode) => {
    setViewMode(value);
    dispatch(
      sendAnalyticEvent({ event: 'image_remove_watermark_modal_view_mode_change_tap', data: { view_mode: value } })
    );
  };

  const handleDownload = () => {
    dispatch(sendAnalyticEvent({ event: 'remove_watermark_proced_click' }));
    onDownload();
  };

  useEffect(() => {
    dispatch(
      sendAnalyticEvent({
        event: 'remove_watermark_modal_view',
        data: { screen_config_name: 'remove_watermark_modal_view' }
      })
    );
  }, [dispatch]);

  return (
    <ModalLayout
      canClose={false}
      dataTestId='remove-watermark-modal'
    >
      <div
        className={classNames(
          'w-[min(calc(100vw-64px),1376px)] h-[min(calc(100vh-64px),_837px)] px-7 py-6 overflow-hidden flex flex-col',
          'max-sm:w-full max-sm:px-4 max-sm:py-4 max-sm:max-h-[90dvh] max-sm:h-auto'
        )}
      >
        <h3
          className={classNames(
            'text-[rgba(0,_0,_0,_0.87)] text-[28px] font-extrabold leading-[34px] pb-6 text-center',
            'max-sm:text-lg max-sm:pb-4 max-sm:text-[22px] max-sm:leading-[28px]'
          )}
        >
          <Trans i18nKey='popups.remove_watermark_popup.title' />
        </h3>

        <DragUploadZone
          description={<Trans i18nKey='popups.remove_watermark_popup.upload_description' />}
          className='h-full overflow-hidden flex-1 flex flex-col max-sm:h-auto'
          formats={formats}
          onUpload={handleUpload}
        >
          <PreviewSwitcher
            className={classNames('mx-auto w-fit mb-6', 'max-sm:mb-4 max-sm:w-full')}
            value={viewMode}
            onChange={onViewModeChange}
          />

          <div className='flex flex-col flex-1 h-full overflow-hidden max-sm:overflow-y-auto max-sm:block'>
            <div className='mb-6 h-full overflow-hidden max-sm:h-auto'>
              {viewMode === EViewMode.SPLIT_SCREEN && (
                <SplitScreenPreview
                  removedWatermarkImageUrl={removedWatermarkImageUrl}
                  originalImageUrl={originalImageUrl}
                />
              )}

              {viewMode === EViewMode.SLIDER && (
                <SliderPreview
                  removedWatermarkImageUrl={removedWatermarkImageUrl}
                  originalImageUrl={originalImageUrl}
                />
              )}
            </div>
            <DragUploadButton
              title={
                <Trans
                  i18nKey='popups.remove_watermark_popup.upload_title'
                  components={{
                    span: <span className='underline' />
                  }}
                />
              }
              titleMobile={
                <Trans
                  i18nKey='popups.remove_watermark_popup.upload_title_mobile'
                  components={{
                    span: <span className='underline' />
                  }}
                />
              }
              description={<Trans i18nKey='popups.remove_watermark_popup.upload_description' />}
              className='max-sm:mb-6'
              formats={formats}
              onUpload={handleUpload}
            />
          </div>
        </DragUploadZone>
        <CommonButton
          type='danger'
          className='w-full h-[56px] !bg-[#D2294B] hover:!bg-[#b61333] mt-4 !rounded-xl'
          onClick={handleDownload}
        >
          <DownloadIcon />
          <Trans i18nKey='popups.remove_watermark_popup.download_button' />
        </CommonButton>
      </div>
    </ModalLayout>
  );
};
